import { useState, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ChevronLeft,
  Save,
  Calendar,
  Flag,
  AlertCircle,
  Loader2,
  Trash2,
  Check
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Input, Select } from '../../components/ui/Input';
import { SearchableSelect } from '../../components/ui/SearchableSelect';
import { useApi } from '../../contexts/ApiContext';
import { useSeries } from '../../App';

export default function EventForm() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const api = useApi();
  const currentSeries = useSeries();
  const seriesSlug = currentSeries?.slug;

  const isEditing = !!eventId;

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    event_date: '',
    promotion_id: '',
    promotion_name: '',
    track_promo_event_status_id: '1',
    series_ids: [],
    description: '',
    notes: ''
  });

  // UI state
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  const { data: dropdownData } = useQuery({
    queryKey: ['event-dropdown-data', isEditing],
    queryFn: async () => {
      // Use the authenticated api wrapper (axios w/ auth interceptor), not raw
      // fetch — these endpoints require a bearer token. The promotion list is
      // ~16k rows; only the create form lets you pick one. Editing can't move an
      // event to another promotion, so skip that fetch entirely there.
      const [statusesData, seriesData, promotionsData] = await Promise.all([
        api.apiCall('/api/events/data/statuses'),
        api.apiCall('/api/series'),
        isEditing ? Promise.resolve([]) : api.apiCall('/api/events/data/promotions')
      ]);

      return {
        promotions: Array.isArray(promotionsData) ? promotionsData : [],
        statuses: Array.isArray(statusesData) ? statusesData : [],
        seriesList: Array.isArray(seriesData?.series)
          ? seriesData.series
          : (Array.isArray(seriesData) ? seriesData : [])
      };
    },
  });

  const promotions = dropdownData?.promotions || [];
  const statuses = dropdownData?.statuses || [];
  const seriesList = dropdownData?.seriesList || [];

  const promotionOptions = useMemo(
    () => promotions.map(p => ({ value: p.promotion_ID, label: p.name })),
    [promotions]
  );

  const { data: loadedEvent, isLoading: loading } = useQuery({
    queryKey: ['event-edit', eventId],
    queryFn: async () => {
      const result = await api.events.getById(eventId);
      if (!result.success || !result.data) throw new Error(result.message || 'Failed to load event');
      return result.data.event || result.data;
    },
    enabled: isEditing,
  });

  // Seed the editable form from the loaded event during render, not inside the
  // queryFn. With a 5-minute staleTime, a remount that serves cached data skips
  // queryFn entirely, so seeding there would leave the form blank.
  const [seededEventId, setSeededEventId] = useState(null);
  if (loadedEvent && seededEventId !== eventId) {
    setSeededEventId(eventId);
    setFormData({
      name: loadedEvent.name || '',
      event_date: loadedEvent.event_date ? loadedEvent.event_date.split('T')[0] : '',
      promotion_id: loadedEvent.promotion_ID?.toString() || '',
      promotion_name: loadedEvent.promotion_name || '',
      track_promo_event_status_id: loadedEvent.track_promo_event_status_ID?.toString() || '1',
      series_ids: Array.isArray(loadedEvent.series) ? loadedEvent.series.map(s => s.id) : [],
      description: loadedEvent.description || '',
      notes: loadedEvent.notes || ''
    });
  }

  // Handle input changes
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // Handle series selection
  const handleSeriesToggle = (seriesId) => {
    setFormData(prev => ({
      ...prev,
      series_ids: prev.series_ids.includes(seriesId)
        ? prev.series_ids.filter(id => id !== seriesId)
        : [...prev.series_ids, seriesId]
    }));
  };

  // Validate form
  const validate = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Event name is required';
    if (!formData.event_date) errors.event_date = 'Event date is required';
    if (!formData.promotion_id) errors.promotion_id = 'Promotion is required';
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    setError(null);

    try {
      const submitData = {
        name: formData.name,
        event_date: formData.event_date,
        track_promo_event_status_id: parseInt(formData.track_promo_event_status_id),
        description: formData.description,
        notes: formData.notes
      };

      // Promotion and series are only set at creation. PUT ignores promotion_ID,
      // and it rewrites race_event_racing_entity destructively and isn't
      // multi-series-aware, so editing sends neither.
      if (!isEditing) {
        submitData.promotion_id = parseInt(formData.promotion_id);
        submitData.series_ids = formData.series_ids;
      }

      let result;
      if (isEditing) {
        result = await api.events.update(eventId, submitData);
      } else {
        result = await api.events.create(submitData);
      }

      if (result.success) {
        const newEventId = isEditing ? eventId : result.data?.event?.id || result.data?.id;
        navigate(`/${seriesSlug}/events/${newEventId}`);
      } else {
        setError(result.message || 'Failed to save event');
      }
    } catch (err) {
      setError('Failed to save event');
      console.error('Error saving event:', err);
    } finally {
      setSaving(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this event? This action cannot be undone.')) {
      return;
    }

    setSaving(true);
    try {
      const result = await api.events.delete(eventId);
      if (result.success) {
        navigate(`/${seriesSlug}/events`);
      } else {
        setError(result.message || 'Failed to delete event');
      }
    } catch (err) {
      setError('Failed to delete event');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="animate-fade-in">
        <div className="mb-4">
          <div className="h-6 w-48 bg-slate-200 rounded animate-pulse" />
        </div>
        <Card className="animate-pulse">
          <CardContent className="p-4">
            <div className="space-y-3">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="h-10 bg-slate-100 rounded" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      {/* Page Header */}
      <div className="mb-4">
        <Link
          to={isEditing ? `/${seriesSlug}/events/${eventId}` : `/${seriesSlug}/events`}
          className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 mb-3"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
          {isEditing ? 'Back to Event' : 'Back to Events'}
        </Link>

        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-lg font-semibold text-slate-800">
              {isEditing ? 'Edit Event' : 'Create Event'}
            </h1>
            <p className="text-xs text-slate-500">
              {isEditing ? 'Update event details' : 'Schedule a new racing event'}
            </p>
          </div>
          {isEditing && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDelete}
              disabled={saving}
              className="text-red-500 hover:text-red-600 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4 mr-1.5" />
              Delete
            </Button>
          )}
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded flex items-center gap-2.5">
          <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
          <span className="text-sm text-red-700">{error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-4">
            {/* Basic Info */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-sm">
                  <Calendar className="w-4 h-4 text-wrg-blue-600" />
                  Event Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Input
                  label="Event Name"
                  placeholder="e.g., Spring Showdown at..."
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  error={validationErrors.name}
                  required
                  size="sm"
                />

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <Input
                    type="date"
                    label="Event Date"
                    value={formData.event_date}
                    onChange={(e) => handleChange('event_date', e.target.value)}
                    error={validationErrors.event_date}
                    required
                    size="sm"
                  />

                  <Select
                    label="Status"
                    value={formData.track_promo_event_status_id}
                    onChange={(e) => handleChange('track_promo_event_status_id', e.target.value)}
                    size="sm"
                  >
                    {statuses.map(status => (
                      <option key={status.track_promo_event_status_ID} value={status.track_promo_event_status_ID}>
                        {status.name}
                      </option>
                    ))}
                  </Select>
                </div>

                {isEditing ? (
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">
                      Promotion
                    </label>
                    <div className="px-3 py-2 bg-slate-50 border border-slate-200 rounded text-sm text-slate-700">
                      {formData.promotion_name || '-'}
                    </div>
                    <p className="text-xs text-slate-400 mt-1">
                      Promotion is set when the event is created and cannot be changed here.
                    </p>
                  </div>
                ) : (
                  <SearchableSelect
                    label="Promotion"
                    value={formData.promotion_id}
                    onChange={(val) => handleChange('promotion_id', String(val))}
                    options={promotionOptions}
                    placeholder="Search promotions..."
                    error={validationErrors.promotion_id}
                    required
                    size="sm"
                  />
                )}

                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    className="w-full px-3 py-2 bg-white border border-slate-300 rounded text-sm
                      text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2
                      focus:ring-wrg-blue-500 focus:border-wrg-blue-500 resize-none"
                    rows={3}
                    placeholder="Additional notes about this event..."
                    value={formData.notes}
                    onChange={(e) => handleChange('notes', e.target.value)}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Series Selection */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-sm">
                  <Flag className="w-4 h-4 text-wrg-blue-600" />
                  Racing Series
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-slate-500 mb-3">
                  {isEditing
                    ? 'Series are set when the event is created and cannot be changed here.'
                    : 'Select which series will be racing at this event'}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {seriesList.map(series => {
                    const isSelected = formData.series_ids.includes(series.racing_entity_ID);
                    if (isEditing && !isSelected) return null;
                    return (
                      <div
                        key={series.racing_entity_ID}
                        onClick={isEditing ? undefined : () => handleSeriesToggle(series.racing_entity_ID)}
                        className={`p-2.5 rounded border transition-colors
                          ${isEditing ? 'cursor-default' : 'cursor-pointer'}
                          ${isSelected
                            ? 'border-wrg-blue-500 bg-wrg-blue-50'
                            : 'border-slate-200 hover:border-slate-300 bg-white'}`}
                      >
                        <div className="flex items-center gap-2.5">
                          <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0
                            ${isSelected
                              ? 'border-wrg-blue-500 bg-wrg-blue-500'
                              : 'border-slate-300'}`}
                          >
                            {isSelected && (
                              <Check className="w-3 h-3 text-white" />
                            )}
                          </div>
                          <span className={`text-sm ${isSelected ? 'text-wrg-blue-700 font-medium' : 'text-slate-700'}`}>
                            {series.name || series.entity_name}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {seriesList.length === 0 && (
                  <p className="text-slate-400 text-sm text-center py-4">No series available</p>
                )}
                {isEditing && seriesList.length > 0 && formData.series_ids.length === 0 && (
                  <p className="text-slate-400 text-sm text-center py-4">No series assigned</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Actions */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button
                  type="submit"
                  className="w-full"
                  size="sm"
                  disabled={saving}
                  leftIcon={saving ? Loader2 : Save}
                >
                  {saving ? 'Saving...' : (isEditing ? 'Save Changes' : 'Create Event')}
                </Button>
                <Link
                  to={isEditing ? `/${seriesSlug}/events/${eventId}` : `/${seriesSlug}/events`}
                  className="block"
                >
                  <Button type="button" variant="ghost" size="sm" className="w-full">
                    Cancel
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Quick Tips */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Tips</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="text-xs text-slate-500 space-y-1.5">
                  <li className="flex items-start gap-1.5">
                    <span className="text-wrg-blue-600 font-bold">•</span>
                    A promotion groups multiple events at the same track
                  </li>
                  <li className="flex items-start gap-1.5">
                    <span className="text-wrg-blue-600 font-bold">•</span>
                    Select all series that will race at this event
                  </li>
                  <li className="flex items-start gap-1.5">
                    <span className="text-wrg-blue-600 font-bold">•</span>
                    You can add races after creating the event
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Series Badge Preview */}
            {formData.series_ids.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Selected Series</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-1.5">
                    {formData.series_ids.map(id => {
                      const series = seriesList.find(s => s.racing_entity_ID === id);
                      return series ? (
                        <Badge key={id} variant="primary" size="sm">
                          {series.name || series.entity_name}
                        </Badge>
                      ) : null;
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}
